import { AssetManager } from "../assets/asset-manager";
import { PreloadQueue } from "../assets/preload-queue";
import { characters, type CharacterDefinition } from "../data/characters";
import { PerfMonitor } from "../perf/perf-monitor";
import { ViewportController } from "./viewport";
import { FlappyScene } from "../scenes/flappy/flappy-scene";
import { SceneManager } from "../scenes/scene";
import { HomeScene } from "../scenes/home/home-scene";
import { FormationScene } from "../scenes/formation/formation-scene";
import { createInitialPartyState, type PartyState } from "../scenes/formation/formation-state";
import type { SceneTransitionDirection } from "../scenes/scene";
import { ProgressiveFontLoader } from "./font-loader";

const backgroundLow = "/assets/home/background-low.webp";
const backgroundHigh = "/assets/home/background-high.webp";
const backgroundLowFallback = "/assets/home/bg-low.svg";
const backgroundHighFallback = "/assets/home/bg-high.svg";
const flappyBackground = "/assets/flappy/background.svg";
const flappyObstacle = "/assets/flappy/obstacle.svg";

export class App {
  private readonly assets = new AssetManager();
  private readonly monitor = new PerfMonitor();
  private readonly fonts = new ProgressiveFontLoader();
  private readonly preloadQueue = new PreloadQueue(this.assets);
  private readonly scenes: SceneManager;
  private readonly viewportController: ViewportController;
  private readonly stage: HTMLElement;
  private partyState: PartyState = createInitialPartyState();
  private characterIndex = 0;
  private homeScene: HomeScene | null = null;

  constructor(private readonly root: HTMLElement, private readonly devMode = false) {
    const shell = document.createElement("div");
    shell.className = "viewport-shell";
    shell.innerHTML = `
      <div class="viewport-scaler">
        <div class="game-stage" aria-live="polite"></div>
      </div>
      <div class="rotate-device-overlay" role="status" hidden aria-hidden="true">
        <div class="rotate-device-icon" aria-hidden="true">↻</div>
        <p>横向きにしてプレイしてください</p>
      </div>
      <pre class="viewport-debug" hidden></pre>
    `;
    root.replaceChildren(shell);
    shell.classList.toggle("is-debug", this.devMode);
    const scaler = shell.querySelector<HTMLElement>(".viewport-scaler")!;
    const stage = shell.querySelector<HTMLElement>(".game-stage")!;
    const rotateOverlay = shell.querySelector<HTMLElement>(".rotate-device-overlay")!;
    const debug = shell.querySelector<HTMLElement>(".viewport-debug")!;
    this.stage = stage;
    this.scenes = new SceneManager(stage);
    this.viewportController = new ViewportController(stage, scaler, rotateOverlay, this.devMode ? debug : undefined);
  }

  async boot(): Promise<void> {
    this.monitor.mark("boot-start");
    const current = this.currentCharacter();
    const initialLowImage = await this.loadImageWithFallback([current.home.lowImage, current.home.fallback?.lowImage]);
    const initialBackground = await this.loadImageWithFallback([backgroundLow, backgroundLowFallback]);
    this.monitor.mark("home-low-visible");
    this.monitor.measure("boot-to-home-low", "boot-start", "home-low-visible");
    await this.showHome(initialBackground ?? backgroundLowFallback, initialLowImage ?? current.home.fallback?.lowImage ?? current.home.lowImage);
    this.schedulePreload();
  }

  private async showHome(
    initialBackground?: string,
    initialLowImage?: string,
    transitionDirection?: SceneTransitionDirection,
    immediateAssets = false,
  ): Promise<void> {
    const current = this.currentCharacter();
    const resolvedBackground = initialBackground ?? (
      immediateAssets
        ? (this.assets.isLoaded(backgroundLow) ? backgroundLow : backgroundLowFallback)
        : (await this.loadImageWithFallback([backgroundLow, backgroundLowFallback])) ?? backgroundLowFallback
    );
    const resolvedLowImage = initialLowImage ?? (
      immediateAssets
        ? (this.assets.isLoaded(current.home.lowImage) ? current.home.lowImage : current.home.fallback?.lowImage ?? current.home.lowImage)
        : (await this.loadImageWithFallback([current.home.lowImage, current.home.fallback?.lowImage])) ?? current.home.lowImage
    );
    this.homeScene = new HomeScene(
      this.assets,
      resolvedBackground,
      backgroundHigh,
      backgroundHighFallback,
      characters,
      resolvedLowImage,
      {
        getCharacter: () => this.currentCharacter(),
        getCharacterIndex: () => this.characterIndex,
        changeCharacter: (direction) => this.changeCharacter(direction),
        selectCharacter: (index) => this.selectCharacter(index),
        play: () => void this.startGame(),
        formation: () => void this.showFormation(),
      },
      this.monitor,
      this.fonts,
      this.devMode,
    );
    if (transitionDirection) await this.scenes.transitionTo(this.homeScene, { direction: transitionDirection });
    else await this.scenes.change(this.homeScene);
  }

  private changeCharacter(direction: -1 | 1): void {
    this.selectCharacter((this.characterIndex + direction + characters.length) % characters.length);
  }

  private selectCharacter(index: number): void {
    if (!Number.isInteger(index) || index < 0 || index >= characters.length || index === this.characterIndex) return;
    this.characterIndex = index;
    this.homeScene?.showCharacter(this.currentCharacter(), this.characterIndex);
    this.enqueueCharacterAssets(this.currentCharacter().home.highImage, this.currentCharacter().home.fallback?.highImage, "UPGRADE");
    this.enqueueCharacterAssets(this.adjacentCharacter(-1).home.lowImage, this.adjacentCharacter(-1).home.fallback?.lowImage, "PREFETCH");
    this.enqueueCharacterAssets(this.adjacentCharacter(1).home.lowImage, this.adjacentCharacter(1).home.fallback?.lowImage, "PREFETCH");
  }

  private schedulePreload(): void {
    this.enqueueCharacterAssets(backgroundHigh, backgroundHighFallback, "UPGRADE");
    this.enqueueCharacterAssets(this.currentCharacter().home.highImage, this.currentCharacter().home.fallback?.highImage, "UPGRADE");
    characters.forEach((character) => this.preloadQueue.enqueue(character.icon, "PREFETCH"));
    this.preloadQueue.enqueue(flappyBackground, "PREFETCH");
    this.preloadQueue.enqueue(flappyObstacle, "PREFETCH");
    this.preloadQueue.enqueue(this.currentCharacter().game.sprite, "PREFETCH");
    const run = () => {
      void this.preloadQueue.drain().then(() => {
        this.monitor.mark("flappy-preloaded");
        this.monitor.measure("interactive-to-flappy-ready", "home-interactive", "flappy-preloaded");
      });
    };
    const idle = (window as Window & { requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number }).requestIdleCallback;
    if (idle) idle(run, { timeout: 1200 });
    else window.setTimeout(run, 120);
  }

  private async startGame(): Promise<void> {
    this.monitor.mark("play-start");
    this.homeScene?.setLoading("preparing game…");
    const current = this.currentCharacter();
    await Promise.allSettled([
      this.assets.loadImage(flappyBackground),
      this.assets.loadImage(flappyObstacle),
      this.assets.loadImage(current.game.sprite),
    ]);
    const game = new FlappyScene(this.assets, current, window.localStorage, this.monitor, {
      home: () => void this.returnHome(),
    });
    await this.scenes.change(game);
  }

  private async showFormation(): Promise<void> {
    const formation = new FormationScene(this.assets, characters, {
      getPartyState: () => this.partyState,
      updatePartyState: (party) => {
        this.partyState = party;
      },
      home: () => void this.returnHomeFromFormation(),
    });
    await this.scenes.transitionTo(formation, { direction: "forward" });
  }

  private async returnHome(): Promise<void> {
    await this.showHome();
    this.monitor.mark("home-returned");
  }

  private async returnHomeFromFormation(): Promise<void> {
    await this.showHome(undefined, undefined, "back", true);
    this.monitor.mark("home-returned");
  }

  private async loadImageWithFallback(urls: Array<string | undefined>): Promise<string | undefined> {
    for (const url of urls) {
      if (!url) continue;
      try {
        await this.assets.loadImage(url);
        return url;
      } catch {
        // Try the compatibility placeholder before giving up.
      }
    }
    return undefined;
  }

  private enqueueCharacterAssets(primary: string, fallback: string | undefined, priority: "UPGRADE" | "PREFETCH"): void {
    this.preloadQueue.enqueue(primary, priority);
    if (fallback) this.preloadQueue.enqueue(fallback, priority);
  }

  private currentCharacter(): CharacterDefinition {
    return characters[this.characterIndex];
  }

  private adjacentCharacter(offset: number): CharacterDefinition {
    return characters[(this.characterIndex + offset + characters.length) % characters.length];
  }
}
